// src/components/ShipmentStatus.jsx
// Reusable component that renders the async shipment state machine.
// Covers all states from FRONTEND_INTEGRATION_GUIDE.md §3 and §4.
// Uses only design tokens — no raw hex, no hardcoded strings.

import React from 'react';
import { Link } from 'react-router-dom';
import { useStrings } from '../i18n';
import PanchTatvaButton from './PanchTatvaButton';

/**
 * Determine which UI state to render based on order data.
 * Returns one of: 'processing' | 'checking' | 'delayed' | 'failed' |
 *                 'permanent_failed' | 'created' | 'in_transit' |
 *                 'out_for_delivery' | 'delivered'
 */
function getUiState(order, pollingSeconds) {
  if (!order) return 'processing';

  const { shiprocket, status } = order;

  if (!shiprocket) return 'processing';

  // Order fully delivered
  if (status === 'delivered') return 'delivered';

  // Shipment exists — determine transit state
  if (shiprocket.shipment_id) {
    const ss = shiprocket.shipping_status;
    if (ss === 'out_for_delivery' || status === 'out-for-delivery') return 'out_for_delivery';
    if (ss === 'in_transit' || status === 'shipped') return 'in_transit';
    return 'created';
  }

  // No shipment yet — use shiprocket.status
  const sr = shiprocket.status;

  if (sr === 'permanent_failed') return 'permanent_failed';
  if (sr === 'failed') return 'failed';
  if (sr === 'failed_reconciling') return 'checking';

  // Still pending/processing — show delayed message after 30s
  if (pollingSeconds > 30) return 'delayed';
  return 'processing';
}

/**
 * @param {Object}   order          - Full order object from backend
 * @param {boolean}  isPolling      - Whether polling is currently active
 * @param {number}   pollingSeconds - Seconds since polling started
 * @param {Function} onRetry        - Called when user clicks "Retry Shipment"
 * @param {Function} onContactSupport - Called when user clicks "Contact Support"
 * @param {boolean}  compact        - Smaller layout for sidebar / card usage
 */
export default function ShipmentStatus({
  order,
  isPolling = false,
  pollingSeconds = 0,
  onRetry,
  onContactSupport,
  compact = false,
}) {
  const strings = useStrings();
  const s = strings.shipment;

  const uiState = getUiState(order, pollingSeconds);
  const shiprocket = order?.shiprocket;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // ─── Processing / Creating ──────────────────────────────────────────────────
  if (uiState === 'processing' || uiState === 'delayed') {
    return (
      <div className={`bg-cardBg rounded-2xl shadow-card ${compact ? 'p-4' : 'p-6'}`}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-secondary/10 flex items-center justify-center">
            <SpinnerIcon className="text-secondary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-sans font-semibold text-headingText ${compact ? 'text-sm' : 'text-base'}`}>
              {s.creatingShipment}
            </p>
            <p className={`font-sans text-textSecondary mt-1 ${compact ? 'text-xs' : 'text-sm'}`}>
              {uiState === 'delayed' ? s.delayedDesc : s.creatingShipmentDesc}
            </p>
            {isPolling && !compact && (
              <p className="font-sans text-xs text-textSecondary mt-2 italic">
                {s.pollingNote}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Checking / Reconciling ─────────────────────────────────────────────────
  if (uiState === 'checking') {
    return (
      <div className={`bg-cardBg rounded-2xl shadow-card ${compact ? 'p-4' : 'p-6'}`}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
            <span className="text-lg">🔍</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-sans font-semibold text-headingText ${compact ? 'text-sm' : 'text-base'}`}>
              {s.checkingStatus}
            </p>
            <p className={`font-sans text-textSecondary mt-1 ${compact ? 'text-xs' : 'text-sm'}`}>
              {s.checkingStatusDesc}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ─── Shipment Failed (transient) ────────────────────────────────────────────
  if (uiState === 'failed') {
    return (
      <div className={`bg-cardBg rounded-2xl shadow-card border border-red-200 ${compact ? 'p-4' : 'p-6'}`}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <span className="text-lg">⚠️</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-sans font-semibold text-headingText ${compact ? 'text-sm' : 'text-base'}`}>
              {s.shipmentFailed}
            </p>
            <p className={`font-sans text-textSecondary mt-1 ${compact ? 'text-xs' : 'text-sm'}`}>
              {s.shipmentFailedDesc}
            </p>
            {onRetry && !compact && (
              <div className="mt-4">
                <PanchTatvaButton variant="outline" size="small" onClick={onRetry}>
                  {s.retryShipment}
                </PanchTatvaButton>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Permanent Failure ──────────────────────────────────────────────────────
  if (uiState === 'permanent_failed') {
    return (
      <div className={`bg-cardBg rounded-2xl shadow-card border border-red-300 ${compact ? 'p-4' : 'p-6'}`}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <span className="text-lg">🆘</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className={`font-sans font-semibold text-headingText ${compact ? 'text-sm' : 'text-base'}`}>
              {s.permanentFailed}
            </p>
            <p className={`font-sans text-textSecondary mt-1 ${compact ? 'text-xs' : 'text-sm'}`}>
              {s.permanentFailedDesc}
            </p>
            {!compact && (
              <div className="mt-4">
                <PanchTatvaButton
                  variant="primary"
                  size="small"
                  onClick={onContactSupport || (() => {})}
                >
                  {s.contactSupport}
                </PanchTatvaButton>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Shipment Created / In Transit / Out For Delivery / Delivered ───────────
  const stateConfig = {
    created: { icon: '✅', title: s.shipmentCreated, desc: s.shipmentCreatedDesc, titleColor: 'text-green-700' },
    in_transit: { icon: '📦', title: s.inTransit, desc: s.inTransitDesc, titleColor: 'text-blue-700' },
    out_for_delivery: { icon: '🚚', title: s.outForDelivery, desc: s.outForDeliveryDesc, titleColor: 'text-purple-700' },
    delivered: { icon: '🎉', title: s.delivered, desc: s.deliveredDesc, titleColor: 'text-green-700' },
  };

  const cfg = stateConfig[uiState] || stateConfig.created;

  return (
    <div className={`bg-cardBg rounded-2xl shadow-card ${compact ? 'p-4' : 'p-6'}`}>
      {/* Status header */}
      <div className="flex items-start gap-4 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
          <span className="text-lg">{cfg.icon}</span>
        </div>
        <div>
          <p className={`font-sans font-semibold ${cfg.titleColor} ${compact ? 'text-sm' : 'text-base'}`}>
            {cfg.title}
          </p>
          <p className={`font-sans text-textSecondary mt-0.5 ${compact ? 'text-xs' : 'text-sm'}`}>
            {cfg.desc}
          </p>
        </div>
      </div>

      {/* Tracking details — only if shipment_id exists */}
      {shiprocket?.shipment_id && !compact && (
        <div className="bg-surface rounded-xl p-4 space-y-2">
          {shiprocket.awb_code && (
            <DetailRow label={s.awbCode} value={shiprocket.awb_code} mono />
          )}
          {shiprocket.courier_name && (
            <DetailRow label={s.courier} value={shiprocket.courier_name} />
          )}
          {shiprocket.shipping_status && (
            <DetailRow label={s.trackingStatus} value={shiprocket.shipping_status} />
          )}
          {shiprocket.estimated_delivery_date && (
            <DetailRow label={s.estimatedDelivery} value={formatDate(shiprocket.estimated_delivery_date)} />
          )}
          {shiprocket.tracking_url && (
            <div className="pt-2">
              <a
                href={shiprocket.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-sans text-sm text-secondary hover:text-secondary/80 transition-colors underline"
              >
                {s.viewTracking}
              </a>
            </div>
          )}
        </div>
      )}

      {/* Compact tracking badge */}
      {shiprocket?.awb_code && compact && (
        <p className="font-sans text-xs text-textSecondary mt-1">
          {s.awbCode}: <span className="font-mono text-bodyText">{shiprocket.awb_code}</span>
        </p>
      )}

      {/* Action buttons */}
      {!compact && (
        <div className="mt-4 flex gap-3 flex-wrap">
          {uiState === 'delivered' && (
            <Link to="/shop">
              <PanchTatvaButton variant="primary" size="small">{s.shopAgain}</PanchTatvaButton>
            </Link>
          )}
          {shiprocket?.tracking_url && uiState !== 'delivered' && (
            <a href={shiprocket.tracking_url} target="_blank" rel="noopener noreferrer">
              <PanchTatvaButton variant="outline" size="small">{s.trackOrder}</PanchTatvaButton>
            </a>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function DetailRow({ label, value, mono = false }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="font-sans text-xs text-textSecondary flex-shrink-0">{label}</span>
      <span className={`font-sans text-sm text-bodyText text-right ${mono ? 'font-mono' : ''}`}>
        {value}
      </span>
    </div>
  );
}

function SpinnerIcon({ className = '' }) {
  return (
    <svg
      className={`w-5 h-5 animate-spin ${className}`}
      fill="none"
      viewBox="0 0 24 24"
      aria-busy="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}
